import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BadgeCheck, CalendarClock, CreditCard, Megaphone, Users } from "lucide-react";
import { usePageMeta } from "@/hooks/use-page-meta";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { isNativeIosApp } from "@/lib/platform";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

function ImageCarousel({
  images,
  title,
  autoplayMs = 3500,
}: {
  images: string[];
  title: string;
  autoplayMs?: number;
}) {
  const [api, setApi] = useState<any>(null);

  useEffect(() => {
    if (!api) return;
    if (images.length <= 1) return;
    const id = window.setInterval(() => {
      api.scrollNext();
    }, autoplayMs);
    return () => window.clearInterval(id);
  }, [api, autoplayMs, images.length]);

  return (
    <Carousel opts={{ loop: true }} setApi={setApi} className="absolute inset-0 w-full h-full">
      <CarouselContent className="h-full">
        {images.map((src) => (
          <CarouselItem key={src} className="h-full">
            <img
              src={src}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                const t = e.target as HTMLImageElement;
                t.style.display = "none";
              }}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="left-3 top-1/2 -translate-y-1/2" />
      <CarouselNext className="right-3 top-1/2 -translate-y-1/2" />
    </Carousel>
  );
}

const businessTypes = [
  {
    id: "salons-barbers",
    title: "Salons & barbers",
    images: ["/hairdresser.png", "/hairbraidng.png", "/barber.png", "/blackbarber.png", "/tatooartist.png", "/makeupartist.png"],
    summary:
      "Stop juggling WhatsApp chats and phone calls. Buukd gives your team one live calendar with automated reminders and clean customer history.",
    bullets: [
      "24/7 online booking with deposit support",
      "No-show reduction with 24h and 2h reminders",
      "Customer history for repeat visits and preferences",
    ],
  },
  {
    id: "nail-techs",
    title: "Nail techs",
    images: ["/nailtech.png"],
    summary:
      "Run your day from one app even as a solo tech. Clients book slots, get reminder texts, and can reschedule without long back-and-forth chats.",
    bullets: [
      "Simple booking page link for Instagram and TikTok bio",
      "Built-in booking codes for confirmations and updates",
      "Service durations and pricing clearly presented",
    ],
  },
  {
    id: "lash-techs",
    title: "Lash techs",
    images: ["/lashtech.png", "/whitelashtech.png"],
    summary:
      "Keep refill cycles organized with fewer missed sessions. Buukd helps you automate communication and keep your premium client experience consistent.",
    bullets: [
      "Automated reminder flow before each appointment",
      "Fast reschedule management from customer links",
      "Clear records for each visit and follow-ups",
    ],
  },
  {
    id: "spas",
    title: "Spas & wellness centers",
    images: [
      "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&w=1200&q=80",
      "/thaimasseuse.png",
    ],
    summary:
      "Coordinate multiple services and staff smoothly. Buukd keeps availability, bookings, and payment tracking in one place for front desk and management.",
    bullets: [
      "Multi-service scheduling in one shared system",
      "Team-friendly operations with role-based access",
      "Revenue and visit insights to monitor growth",
    ],
  },
  {
    id: "food-vendors",
    title: "Food vendors & caterers",
    images: ["/foodvendor.png"],
    summary:
      "Manage online menu orders and catering requests in one place. Buukd helps you reduce missed orders and streamline kitchen and delivery prep.",
    bullets: [
      "Public orders page with cart and shareable links",
      "Buffet/event request forms with all details captured",
      "Order status updates and business analytics for better planning",
    ],
  },
  {
    id: "gyms",
    title: "Gyms & fitness clubs",
    images: ["/Gemini_Generated_Image_wufhnawufhnawufh.png"],
    summary:
      "Handle memberships and daily check-ins without manual logs. Buukd helps your staff onboard members, track access, and communicate updates quickly.",
    bullets: [
      "Membership plans with active date tracking",
      "Access Desk check-in/out for walk-ins and members",
      "Targeted campaign messages for retention and offers",
    ],
  },
  {
    id: "studios-trainers",
    title: "Studios & personal trainers",
    images: [
      "https://images.unsplash.com/photo-1599058917212-d750089bc07e?auto=format&fit=crop&w=1200&q=80",
      "/photostudio.png",
      "/musicstudio.png",
    ],
    summary:
      "Spend more time coaching and less time scheduling. Buukd centralizes bookings, reminders, and payments so your classes stay full.",
    bullets: [
      "Online booking for sessions and classes",
      "Automatic reminder SMS and email touchpoints",
      "Payment-ready setup for deposits and balances",
    ],
  },
];

export default function Solutions() {
  const isNativeIos = isNativeIosApp();

  usePageMeta({
    title: "Solutions by Business Type | Buukd",
    description:
      "See how Buukd helps salons, nail techs, lash techs, spas, gyms, and fitness businesses grow with booking, reminders, memberships, and payments.",
    path: "/solutions",
  });

  return (
    <div className="min-h-screen flex flex-col font-bricolage">
      <PublicHeader />

      <main className="flex-1 pt-20">
        <section className="py-16 lg:py-24 bg-gradient-hero relative overflow-hidden">
          <div className="absolute inset-0 bg-dotted opacity-40" />
          <div className="container-app relative">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Solutions built for every
                <br />
                beauty, wellness, and fitness business
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-3xl mx-auto">
                Buukd is not one-size-fits-all. Explore how each business type uses Buukd to reduce no-shows, save admin time, and grow revenue.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-2">
                {businessTypes.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="inline-flex items-center rounded-full border border-border bg-background/80 px-3 py-1.5 text-xs sm:text-sm text-foreground hover:bg-background transition-colors"
                  >
                    {item.title}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24 bg-background">
          <div className="container-app">
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="h-10 w-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
                  <CalendarClock className="h-5 w-5 text-pink-500" />
                </div>
                <h3 className="mt-3 font-semibold text-foreground">Always-on booking</h3>
                <p className="mt-1 text-sm text-muted-foreground">Let customers book, reschedule, and confirm without waiting for a response.</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="h-10 w-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-pink-500" />
                </div>
                <h3 className="mt-3 font-semibold text-foreground">Built for teams</h3>
                <p className="mt-1 text-sm text-muted-foreground">Staff-friendly workflows for shared calendars, member check-ins, and front desk operations.</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="h-10 w-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-pink-500" />
                </div>
                <h3 className="mt-3 font-semibold text-foreground">Payments + growth</h3>
                <p className="mt-1 text-sm text-muted-foreground">Collect deposits, track balances, and run simple campaigns to bring clients back.</p>
              </div>
            </div>

            <div className="space-y-8">
              {businessTypes.map((item) => (
                <article key={item.id} id={item.id} className="rounded-3xl border border-border bg-card overflow-hidden shadow-soft">
                  <div className="grid lg:grid-cols-2">
                    <div className="relative min-h-[240px] bg-muted">
                      {(item as any).images?.length > 1 ? (
                        <ImageCarousel images={(item as any).images} title={item.title} />
                      ) : (
                        <img
                          src={(item as any).images?.[0]}
                          alt={item.title}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            const t = e.target as HTMLImageElement;
                            t.style.display = "none";
                          }}
                        />
                      )}
                    </div>
                    <div className="p-6 lg:p-8">
                      <div className="inline-flex items-center gap-2 rounded-full bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-600">
                        <BadgeCheck className="h-3.5 w-3.5" />
                        Business Type Solution
                      </div>
                      <h2 className="mt-4 text-3xl font-bold text-foreground">{item.title}</h2>
                      <p className="mt-3 text-muted-foreground">{item.summary}</p>
                      <ul className="mt-5 space-y-2">
                        {item.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-pink-500 shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6 flex flex-wrap gap-3">
                        {!isNativeIos && (
                          <Button asChild variant="gradient">
                            <Link to="/signup">Start free trial</Link>
                          </Button>
                        )}
                        <Button asChild variant="outline">
                          <Link to="/signin">Sign in</Link>
                        </Button>
                      </div>
                      {isNativeIos && (
                        <p className="mt-3 text-xs text-muted-foreground">
                          Access is provided by your organization administrator.
                        </p>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24 bg-subtle-gray">
          <div className="container-app">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-600">
                <Megaphone className="h-3.5 w-3.5" />
                Ready to grow
              </div>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold text-foreground">
                Pick your business type and launch faster with Buukd
              </h2>
              <p className="mt-3 text-muted-foreground">
                From first booking to repeat customers, Buukd gives your team the tools to run smoothly and look professional online.
              </p>
              <div className="mt-8 flex items-center justify-center gap-3">
                {!isNativeIos && (
                  <>
                    <Button asChild variant="gradient" size="pill-lg">
                      <Link to="/signup">Get started</Link>
                    </Button>
                    <Button asChild variant="outline" size="pill-lg">
                      <Link to="/pricing">View pricing</Link>
                    </Button>
                  </>
                )}
                {isNativeIos && (
                  <Button asChild variant="outline" size="pill-lg">
                    <Link to="/signin">Sign in</Link>
                  </Button>
                )}
              </div>
              {isNativeIos && (
                <p className="mt-3 text-xs text-muted-foreground">
                  New organizations are onboarded outside the iOS app.
                </p>
              )}
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
